/* eslint-disable default-param-last */
import { GlobalState } from '@leverege/ui-redux';
import { Obj, Constants, InterfaceActions, InterfaceReducer, Can } from '@leverege/api-redux';
import Util from "../../routes/Util.js";
import { push } from "../../routes/History.js";
import GroupDelegate from "./GroupDelegate.js"; // eslint-disable-line import/no-cycle
import Relationships from "../../dataSource/Relationships.js";
import AbstractItemDelegate from "../../dataSource/AbstractItemDelegate.js";
import HistoryItemDelegate from "./HistoryItemDelegate.js";
import { ImagineComparatorConverterFactory } from "./Factory.js";
import RelationshipUrlMutatorFactory from "./urlMutators/RelationshipUrlMutatorFactory.js";
import DelegateActions from "./constants/DelegateActions.js";
import LocalCanResolver from "./LocalCanResolver.js";
export default class ItemDelegate extends AbstractItemDelegate {
  static TYPE = 'imagine.ItemDelegate';
  constructor(options) {
    super(options);
    const {
      match,
      relationship
    } = options;
    this.match = match;
    this.relationship = relationship.isResolved ? relationship : relationship.createResolved(match);
    this.systemId = relationship.systemId;
    this.id = this.relationship.id;
    this.localCanResolver = new LocalCanResolver({
      relationship,
      match
    });
    this.type = ItemDelegate.TYPE;
    Object.defineProperties(this, {
      historyDelegate: {
        get: () => {
          if (!this.hDelegate) {
            this.hDelegate = new HistoryItemDelegate(options, this);
          }
          return this.hDelegate;
        }
      }
    });
    if (relationship.match == null) {
      throw new Error('ItemDelegate requires a resolved Relationship');
    }
  }
  can(action) {
    return new Can(this.getInterfaceUrl(action), false, false);
  }
  getGroup() {
    return this.parent();
  }
  itemRef() {
    return this.relationship.createItem(this.relationship.match, this.id).objRef;
  }

  /**
   * Returns the parent's action object. 
   */
  parent() {
    const {
      parent
    } = this.relationship;
    if (parent == null) {
      return null;
    }
    // if parent is an item or an alias, use ItemDelegate
    if (parent.isItem || parent.isAlias) {
      return new ItemDelegate({
        relationship: parent,
        // .createResolved( this.match ), 
        match: this.match,
        ...this.options
      });
    }
    return new GroupDelegate({
      relationship: parent,
      // .createResolved( this.match ), 
      match: this.match,
      ...this.options
    });
  }

  /**
   * Returns a action object for the child attribute. If the attribute
   * is a group relationship, a GroupDelegate object is returned. If it
   * is a one to one relationship, an ItemDelegate will be returned. If
   * it is not a relationship, null is returned
   */
  child(attrPath) {
    let rel = Relationships.getChildByPath(this.relationship, attrPath);
    if (rel == null) {
      return null;
    }
    rel = rel.createResolved(this.match);
    if (rel.isGroup) {
      return new GroupDelegate({
        relationship: rel,
        match: this.match
      });
    }
    return new ItemDelegate({
      relationship: rel,
      match: this.match,
      id: attrPath
    });
  }

  /**
   * Creates a ApiRedux Obj, used to do most of the work
   */
  createObj(action) {
    const pUrl = this.getInterfaceUrl(action, this.relationship.ref);
    return Obj.create(pUrl, this.id, this.relationship.attribute != null);
  }

  /**
   * Returns the url for the interface for this action, resolved
   * to reference the ids supplied by the patch object. This will
   * include the systemId.
   */
  getInterfaceUrl(action, url = this.relationship.objRefUrl) {
    let nUrl = url;
    if (action) {
      const mutators = this.relationship.interfaceUrlMutators?.[action]?.map(mutator => RelationshipUrlMutatorFactory.create(mutator)) || [];
      for (let i = 0; i < mutators.length; i++) {
        nUrl = mutators[i]?.(nUrl);
      }
    }
    return nUrl;
  }
  getOverrides(action) {
    return this.relationship.interfaceOverrides?.[action];
  }
  getDeviceByNetworkAlias(networkId, externalId, identifier = 'deviceId') {
    return this.createObj().getDeviceByNetworkAlias(networkId, identifier, externalId);
  }

  /**
   * Returns the url for this group action object
   */
  getUrl(forInterfaceUrl = false, action) {
    if (forInterfaceUrl) {
      return this.getInterfaceUrl(action);
    }
    return Util.resolve(this.relationship.urlPath, this.match);
  }

  /**
   * Returns a redux action that will call api to read a child of the current path
   * @param {object} data values of the item to be changed
   * @param {object} options api options
   * @returns {function} redux thunk function
   */
  get(options) {
    return InterfaceActions.get(this.getInterfaceUrl(DelegateActions.READ), this.getInterfaceUrl(DelegateActions.READ, this.relationship.ref), options, null, this.getOverrides(DelegateActions.READ));
  }
  canConvertComparator(cmp, cxt) {
    return ImagineComparatorConverterFactory.get(cmp) != null;
  }

  /**
   * Returns a redux action that will ask the api if you can read the item at the current path
   * @param {object} query  query object to send to the api
   */
  requestCanGet(query) {
    const can = new Can(this.getInterfaceUrl(DelegateActions.READ), false, false);
    return can.get(query);
  }

  /**
   * Returns a redux action that will call the api to create an item at the current path
   * @param {object} data the data of the item to create
   * @param {object} options a set of options
   */
  create(data, options) {
    return InterfaceActions.create(this.getInterfaceUrl(DelegateActions.CREATE), data, this.id, Constants.INTERFACE_CREATE_SET, {
      resolveOnWrite: true,
      refresh: 'wait_for',
      ...options
    }, this.getOverrides(DelegateActions.CREATE));
    // return this.createObj( DelegateActions.CREATE ).create( data, options )
  }

  /**
   * Returns a redux action that will ask the api if you can create an item at the current path
   * @param {object} query  query object to send to the api
   * @param {object} body the data of the object to create
   */
  requestCanCreate(query, body) {
    const can = new Can(this.getInterfaceUrl(DelegateActions.CREATE), false, false);
    return can.create(query, body);
  }

  /**
   * Returns a redux action that will call api to update a child of the current path
   * @param {object} data values of the item to be changed
   * @param {object} options api options
   * @returns {function} redux thunk function
   */
  update(data, options) {
    return InterfaceActions.update(this.getInterfaceUrl(DelegateActions.UPDATE), data, this.getInterfaceUrl(DelegateActions.UPDATE, this.relationship.ref), this.id, Constants.INTERFACE_UPDATE, {
      resolveOnWrite: true,
      refresh: 'wait_for',
      ...options
    }, this.getOverrides(DelegateActions.UPDATE));
    // return this.createObj( DelegateActions.UPDATE ).update( data, options )
  }

  /**
   * Returns a redux action that will ask the api if you can update the item at the current path
   * @param {object} query  query object to send to the api
   * @param {object} body the data of the update
   */
  requestCanUpdate(query, body) {
    const can = new Can(this.getInterfaceUrl(DelegateActions.UPDATE), false, false);
    return can.update(query, body);
  }

  /**
   * Returns a redux action that will call api to delete a child of the current path
   * @param {object} options api options
   * @returns {function} redux thunk function
   */
  delete(body, options, reducerOptions) {
    return InterfaceActions.remove(this.getInterfaceUrl(DelegateActions.DELETE), this.id, body, options, reducerOptions, {
      ...this.getOverrides(DelegateActions.DELETE),
      pathIncludesId: true,
      extraValues: {
        /* this ensures that if delete and search urls are different, then
           removal of an item from a list is reflected on search path
          */
        parentPath: this.parent()?.getInterfaceUrl(DelegateActions.SEARCH),
        ...this.getOverrides(DelegateActions.DELETE)?.extraValues
      }
    });
    // return this.createObj( DelegateActions.DELETE ).delete( body, options, reducerOptions )
  }

  /**
   * Returns a redux action that will ask the api if you can delete the item at the current path
   * @param {object} query  query object to send to the api
   * @param {object} body body of the request to test
   * @param {string} body.deleteDevice optional, if set to 'force' will delete
   *                                   the device and not just the relationship to it at a deep path
   */
  requestCanDelete(query, body) {
    const can = new Can(this.getInterfaceUrl(DelegateActions.DELETE), false, false);
    return can.delete(query, body);
  }

  /**
   * Returns a redux action that makes an api call to get the history of an object at the path
   * @param {object} query.query query params that the server will use 
   * @param {string} query.queryName query identifier 
   * @param {object} options api options
   * @returns {function} redux thunk function
   * @deprecated
   */
  history(query, options) {
    return this.historyDelegate.search(query, options);
  }

  /**
   * 
   * @param {*} state 
   * @param {*} requestIfUnavailable 
   * @returns 
   * @deprecated
   */
  canHistory(state = GlobalState.get(), requestIfUnavailable = true) {
    return this.historyDelegate.canList(state, requestIfUnavailable);
  }

  /**
   * Returns a redux action that will ask the api if you can get history at the current path
   * @param {object} query  query object to send to the api
   * @deprecated
   */
  requestCanHistory(query) {
    return this.historyDelegate.requestCanList(query);
  }

  /**
   * 
   * @deprecated
   */
  // eslint-disable-next-line default-param-last
  getHistory(state = GlobalState.get(), queryName, options) {
    return this.historyDelegate.getList(state, queryName, options);
  }

  /**
   * Returns a redux action that makes an api call to get the events of an object at the path
   * @param {object} query.query query params that the server will use 
   * @param {string} query.queryName query identifier 
   * @param {object} options api options
   * @returns {function} redux thunk function
   */
  events(query, options) {
    return this.createObj().events(query, options);
  }

  /**
   * Returns a redux action that will ask the api if you can get events at the current path
   * @param {object} query  query object to send to the api
   */
  requestCanEvents(query) {
    const can = new Can(this.getInterfaceUrl(), false, false);
    return can.events(query);
  }

  /**
   * Returns an api interface object with methods to interact with the specified resource
   * @param {object} path the path to the resource desired to interact with
   * @param {object} options api options
   * @returns {object} function with several methods that return functions for redux thunk 
   */
  resource(path, options) {
    return this.createObj().rsrc(path, options);
  }

  /**
   * Returns a Users object, used to access users with permissions on this device
   */
  apiAccessKeys() {
    return this.createObj().apiAccessKeys();
  }

  /**
   * Returns an Api Access object, used to access a api access key with permissions on this device
   */
  apiAccessKey(apiAccessKeyId) {
    return this.createObj().apiAccessKey(apiAccessKeyId);
  }

  /**
   * Returns an Api Access object, used to access api access keys with permissions on this device
   */
  users() {
    return this.createObj().users();
  }

  /**
   * Returns a User object, used to access a user with permissions on this device
   */
  user(userId) {
    return this.createObj().user(userId);
  }

  /**
   * Returns an Api Access object, used to access api access keys with permissions on this device
   */
  temporaryUsers() {
    return this.createObj().temporaryUsers();
  }

  /**
   * Returns a User object, used to access a user with permissions on this device
   */
  temporaryUser(userId) {
    return this.createObj().temporaryUser(userId);
  }

  /**
   * Returns an Rules object, used to access rules with permissions on this device
   */
  rules() {
    return this.createObj().rules();
  }

  /**
   * Returns a Rules object, used to access a rule with permissions on this device
   */
  rule(ruleId) {
    return this.createObj().rule(ruleId);
  }

  /**
   * makes an api access object for managing a device's outbound messages
   * @returns {object} the api access object
   */
  messages() {
    return this.createObj().messages();
  }
  goTo(attrPath) {
    const {
      blueprint
    } = this.relationship;
    if (attrPath) {
      const attr = blueprint.attributes.find(a => a.path === attrPath);
      if (attr == null || !attr.isRelationship()) {
        return {
          type: 'noop'
        };
      }
      const url = this.getUrl();
      return push(`${url}/${attrPath}`);
    }
    const url = this.getUrl();
    return push(url);
  }

  // local access
  getStatus(state = GlobalState.get()) {
    return InterfaceReducer.getStatus(state, this.getInterfaceUrl(DelegateActions.READ));
  }
  getItem(state = GlobalState.get(), skipPathCheck = false) {
    if (this.relationship.isGroup) {
      const data = state.models?.lookup[this.id];
      if (data == null || data && skipPathCheck) {
        return data;
      }
    }
    return InterfaceReducer.getObject(state, this.getInterfaceUrl(DelegateActions.READ));
  }
  isLoading(state = GlobalState.get()) {
    return InterfaceReducer.isLoading(state, this.getInterfaceUrl(DelegateActions.READ));
  }
  isDone(state = GlobalState.get()) {
    return InterfaceReducer.isDone(state, this.getInterfaceUrl(DelegateActions.READ));
  }

  // accessors

  /**
   * Accesses whether or not a user can create an item at this path in the api from state
   * @param {object} state redux state
   */
  canCreate(query, body, state = GlobalState.get(), requestIfUnavailable = true) {
    const can = this.can(DelegateActions.CREATE).getCreate(state, body);
    const localCan = this.localCanResolver.genericCan(DelegateActions.DELETE, query, body, {
      allowed: can
    });
    if (can == null && localCan == null && requestIfUnavailable) {
      GlobalState.dispatch(this.requestCanCreate(query, body));
    }
    return can ?? localCan;
  }

  /**
   * Accesses whether or not a user can get an item at this path in the api from state
   * @param {object} state redux state
   */
  canGet(state = GlobalState.get(), requestIfUnavailable = true) {
    const can = this.can(DelegateActions.READ).getGet(state);
    if (can == null && requestIfUnavailable) {
      GlobalState.dispatch(this.requestCanGet());
    }
    return can;
  }

  /**
   * Accesses whether or not a user can update an item at this path in the api from state
   * @param {object} state redux state
   */
  canUpdate(state = GlobalState.get(), requestIfUnavailable = true) {
    const can = this.can(DelegateActions.UPDATE).getUpdate(state);
    const localCan = this.localCanResolver.genericCan(DelegateActions.UPDATE, null, null, {
      allowed: can
    });
    if (can == null && localCan == null && requestIfUnavailable) {
      GlobalState.dispatch(this.requestCanUpdate());
    }
    return can ?? localCan;
  }

  /**
   * Accesses whether or not a user can delete an item at this path in the api from state
   * @param {object} state redux state
   */
  canDelete(query, body, state = GlobalState.get(), requestIfUnavailable = true) {
    const can = this.can(DelegateActions.DELETE).getDelete(state, body);
    const localCan = this.localCanResolver.genericCan(DelegateActions.DELETE, query, body, {
      allowed: can
    });
    if (can == null && localCan == null && requestIfUnavailable) {
      GlobalState.dispatch(this.requestCanDelete(query, body));
    }
    return can ?? localCan;
  }

  /**
   * Quickly checks the redux state for the Item's data
   */
  static getItem(id, state = GlobalState.get()) {
    return state.models?.lookup[id];
  }
}